import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'

import useApi from '../../../hooks/useApi'
import Icon from '../../../components/icon'
import EzTooltip from '../../../components/tooltip'
import CheckInOutModal from './CheckInOutModal'
import styles from './volunteerList.module.scss'

export default function CheckInOutButton({
  volunteer,
  event,
  canManage,
  onUpdate,
}) {
  const { t } = useTranslation()
  const { volunteerCheckIn, volunteerCheckOut } = useApi()

  const [isLoading, setIsLoading] = useState(false)
  const [showCheckInModal, setShowCheckInModal] = useState(false)
  const [showCheckOutModal, setShowCheckOutModal] = useState(false)
  const [optimisticAction, setOptimisticAction] = useState(null) // this is for optimistic showing of ui change
  const isProcessingRef = useRef(false)
  const shouldKeepOptimisticRef = useRef(false)

  const buttonState = useMemo(() => {
    if (shouldKeepOptimisticRef.current && optimisticAction === 'out') {
      return { state: 'checked_out', canInteract: false }
    }
    if (shouldKeepOptimisticRef.current && optimisticAction === 'in') {
      return { state: 'checked_in', canInteract: false }
    }

    if (optimisticAction === 'out') {
      return { state: 'checked_out', canInteract: false }
    }
    if (optimisticAction === 'in') {
      return { state: 'checked_in', canInteract: false }
    }

    if (volunteer.check_out_time) {
      return { state: 'checked_out', canInteract: false }
    }
    if (volunteer.check_in_time) {
      return { state: 'checked_in', canInteract: true }
    }
    return { state: 'signed_up', canInteract: true }
  }, [volunteer.check_out_time, volunteer.check_in_time, optimisticAction])

  // this prevents the color from flipping back before server data is updated
  useEffect(() => {
    if (!shouldKeepOptimisticRef.current) return

    if (optimisticAction === 'in' && volunteer.check_in_time) {
      shouldKeepOptimisticRef.current = false
      setOptimisticAction(null)
    }

    if (optimisticAction === 'out' && volunteer.check_out_time) {
      shouldKeepOptimisticRef.current = false
      setOptimisticAction(null)
    }
  }, [volunteer.check_in_time, volunteer.check_out_time, optimisticAction])

  const handleCheck = useCallback(
    async type => {
      if (!volunteer?.id || !event?.id) {
        isProcessingRef.current = false
        return
      }

      setIsLoading(true)

      const time = moment().toISOString()

      setOptimisticAction(type)

      onUpdate?.({
        id: volunteer.id,
        optimisticLock: true,
        optimisticAction: type,
      })

      try {
        let response
        if (type === 'in') {
          response = await volunteerCheckIn({
            eventId: event.id,
            volunteerId: volunteer.id,
            checkInTime: time,
          })
        } else {
          response = await volunteerCheckOut({
            eventId: event.id,
            volunteerId: volunteer.id,
            checkOutTime: time,
          })
        }

        const isError = response?.success === false || response?.error
        const isSuccess =
          !isError && response?.status >= 200 && response?.status < 300

        if (isError) {
          const errorStatus =
            response?.error?.response?.status || response?.error?.status

          if (errorStatus === 406) {
            console.warn(
              `[CheckInOut] 406 received — treating as async success`,
            )

            shouldKeepOptimisticRef.current = true

            return
          } else {
            console.error(
              `[CheckInOut] Error checking ${type} (status: ${errorStatus}):`,
              response.error || response,
            )
            onUpdate?.()
          }
          return
        }

        if (!isSuccess) {
          console.warn(
            `[CheckInOut] Unexpected response for ${type} (status: ${response?.status}):`,
            response,
          )
          onUpdate?.()
          return
        }

        if (response?.data) {
          onUpdate?.(response.data)
        } else {
          onUpdate?.()
        }
      } catch (error) {
        console.error(`Error checking ${type}:`, error)
        onUpdate?.()
      } finally {
        if (!shouldKeepOptimisticRef.current) {
          setOptimisticAction(null)
        }
        setIsLoading(false)
        isProcessingRef.current = false
      }
    },
    [volunteer, event?.id, onUpdate, volunteerCheckIn, volunteerCheckOut],
  )

  const handleClick = useCallback(() => {
    if (!canManage) return
    if (!buttonState.canInteract) return
    if (buttonState.state === 'checked_in') {
      setShowCheckOutModal(true)
    } else if (buttonState.state === 'signed_up') {
      setShowCheckInModal(true)
    }
  }, [canManage, buttonState])

  const handleConfirmCheckIn = useCallback(() => {
    if (isProcessingRef.current) {
      return
    }
    isProcessingRef.current = true
    setShowCheckInModal(false)
    handleCheck('in')
  }, [handleCheck])

  const handleConfirmCheckOut = useCallback(() => {
    if (isProcessingRef.current) {
      return
    }
    isProcessingRef.current = true
    setShowCheckOutModal(false)
    handleCheck('out')
  }, [handleCheck])

  const tooltipText = useMemo(() => {
    if (buttonState.state === 'checked_out') {
      return 'Checked Out'
    }
    if (buttonState.state === 'checked_in') {
      return canManage
        ? t('VolunteerListScreen.check_out_button', 'Check Out')
        : 'Checked In'
    }
    return canManage
      ? t('VolunteerListScreen.check_in_button', 'Check In')
      : 'Signed Up'
  }, [buttonState.state, canManage, t])

  return (
    <>
      <span className={styles.actionButtonWrapper}>
        <EzTooltip
          id={`check-in-tooltip-${volunteer.id}`}
          innerContent={tooltipText}
          placement="top"
          outerContent={
            <button
              className={`${styles.actionButton} ${styles[buttonState.state]}`}
              onClick={handleClick}
              disabled={isLoading || !buttonState.canInteract || !canManage}
              type="button"
              aria-label={tooltipText}
            >
              <Icon name="clock" />
            </button>
          }
        />
      </span>
      {canManage && (
        <>
          <CheckInOutModal
            show={showCheckInModal}
            onHide={() => {
              if (!isProcessingRef.current) {
                setShowCheckInModal(false)
              }
            }}
            onConfirm={handleConfirmCheckIn}
            isCheckIn={true}
            volunteerName={volunteer.user?.name || 'Volunteer'}
            disabled={isProcessingRef.current || isLoading}
          />
          <CheckInOutModal
            show={showCheckOutModal}
            onHide={() => {
              if (!isProcessingRef.current) {
                setShowCheckOutModal(false)
              }
            }}
            onConfirm={handleConfirmCheckOut}
            isCheckIn={false}
            volunteerName={volunteer.user?.name || 'Volunteer'}
            disabled={isProcessingRef.current || isLoading}
          />
        </>
      )}
    </>
  )
}
